import React, { useEffect } from 'react';
import { NavigateOptions, To, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Drawer, Tooltip, Typography } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import {
    faBars,
    faCalendarClock,
    faCars,
    faFileInvoiceDollar,
    faHome,
    faSignIn,
    faSignOut,
    faSquareUser,
} from '@fortawesome/pro-regular-svg-icons';
import { useGlobal } from '../units/global/GlobalProvider';
import { EnumIcon, EnumIconMap } from '../data/IconMap';
import { MenuConfig } from '../api/menu-config';
import { useStyles } from '../hooks/useStyles';
import { useScreenSize } from '../hooks/useScreenSize';
import { Auth, useAuth } from '../units/auth/AuthProvider';

export const useSideBar = () => {
    const classes = useStyles();
    const {
        minify,
        setMinify,
        hideText,
        setHideText,
        expandedItems,
        setExpandedItems,
        userGroups,
        setUserGroups,
        reloadUserGroups,
        menuConfigs,
        setMenuConfigs,
        reloadMenuConfigs,
    } = useGlobal();
    const auth = useAuth();
    const screenSize = useScreenSize();
    const navigate = useNavigate();
    const navigateTo = (to: To, options?: NavigateOptions | undefined) => () => navigate(to, options);
    const canSeeMenu = (auth: Auth, menuConfig: MenuConfig): boolean => {
        return (
            userGroups.filter((userGroup) => {
                return (
                    (userGroup.users || []).map((v) => v.userId).includes(auth.profile.userId) &&
                    (userGroup.menuConfigs || []).map((v) => v.menuConfigId).includes(menuConfig.menuConfigId)
                );
            }).length > 0
        );
    };
    const toggleMinify = (isMinify: boolean) => () => setMinify(isMinify);
    const onExpandedItemsChange = (event: React.SyntheticEvent, itemIds: string[]) => setExpandedItems(itemIds);
    const createMenu = (menuConfigs: MenuConfig[], parentId: string = 'root') => {
        return menuConfigs
            .filter((menuConfig) => menuConfig.enable == 'Y' && menuConfig.parentId == parentId && canSeeMenu(auth, menuConfig))
            .map((menuConfig) => (
                <TreeItem
                    key={menuConfig.id}
                    itemId={menuConfig.menuConfigId!}
                    label={
                        <Tooltip
                            title={<Typography variant={'h6'}>{menuConfig.descr}</Typography>}
                            placement='right'
                            disableHoverListener={!hideText}
                        >
                            <div style={{ display: 'inline-block' }}>
                                {menuConfig.iconValue ? (
                                    <FontAwesomeIcon icon={EnumIconMap[menuConfig.iconValue as EnumIcon]} fixedWidth />
                                ) : (
                                    <></>
                                )}
                                {!hideText && menuConfig.descr}
                            </div>
                        </Tooltip>
                    }
                    onClick={menuConfig.navigateTo ? navigateTo(menuConfig.navigateTo) : () => {}}
                >
                    {createMenu(menuConfigs, menuConfig.id)}
                </TreeItem>
            ));
    };
    useEffect(() => {
        const delayResponse = setTimeout(() => setHideText(minify), minify ? 0 : 300);
        return () => clearTimeout(delayResponse);
    }, [minify]);
    return {
        classes,
        minify,
        setMinify,
        hideText,
        setHideText,
        expandedItems,
        setExpandedItems,
        userGroups,
        setUserGroups,
        reloadUserGroups,
        menuConfigs,
        setMenuConfigs,
        reloadMenuConfigs,
        auth,
        screenSize,
        navigate,
        navigateTo,
        canSeeMenu,
        toggleMinify,
        onExpandedItemsChange,
        createMenu,
    };
};

export interface SideBarProps extends ReturnType<typeof useSideBar> {}

export const SideBar = (props: SideBarProps) => {
    return props.screenSize.xsUp ? (
        <SimpleTreeView
            className={props.classes.tree}
            expandedItems={props.expandedItems}
            onExpandedItemsChange={props.onExpandedItemsChange}
            sx={{ transition: 'all 0.3s ease' }}
        >
            {props.auth.isAuth && (
                <TreeItem
                    itemId='home'
                    label={
                        <Tooltip
                            title={<Typography variant={'h6'}>{'首頁'}</Typography>}
                            placement='right'
                            disableHoverListener={!props.hideText}
                        >
                            <div style={{ display: 'inline-block' }}>
                                <FontAwesomeIcon icon={faHome} fixedWidth />
                                {!props.hideText && '首頁'}
                            </div>
                        </Tooltip>
                    }
                    onClick={props.navigateTo('/home')}
                />
            )}
            {props.auth.isAuth && (
                <TreeItem
                    itemId='profile'
                    label={
                        <Tooltip
                            title={<Typography variant={'h6'}>{'我的資料'}</Typography>}
                            placement='right'
                            disableHoverListener={!props.hideText}
                        >
                            <div style={{ display: 'inline-block' }}>
                                <FontAwesomeIcon icon={faSquareUser} fixedWidth />
                                {!props.hideText && '我的資料'}
                            </div>
                        </Tooltip>
                    }
                    onClick={props.navigateTo('/profile')}
                />
            )}
            {/* {props.auth.isAuth && (
                <TreeItem
                    itemId='reservation'
                    label={
                        <Tooltip
                            title={<Typography variant={'h6'}>{'候位洗車'}</Typography>}
                            placement='right'
                            disableHoverListener={!props.hideText}
                        >
                            <div style={{ display: 'inline-block' }}>
                                <FontAwesomeIcon icon={faCalendarClock} fixedWidth />
                                {!props.hideText && '候位洗車'}
                            </div>
                        </Tooltip>
                    }
                    onClick={props.navigateTo('/reservation')}
                />
            )} */}
            {props.auth.isAuth && (
                <TreeItem
                    itemId='reservation'
                    label={
                        <Tooltip
                            title={<Typography variant={'h6'}>{'候位洗車'}</Typography>}
                            placement='right'
                            disableHoverListener={!props.hideText}
                        >
                            <div style={{ display: 'inline-block' }}>
                                <FontAwesomeIcon icon={faCalendarClock} fixedWidth />
                                {!props.hideText && '候位洗車'}
                            </div>
                        </Tooltip>
                    }
                    onClick={props.navigateTo('/simple-reservation')}
                />
            )}
            <TreeItem
                itemId='car-queue-dashboard'
                label={
                    <Tooltip
                        title={<Typography variant={'h6'}>{'排序看板'}</Typography>}
                        placement='right'
                        disableHoverListener={!props.hideText}
                    >
                        <div style={{ display: 'inline-block' }}>
                            <FontAwesomeIcon icon={faCars} fixedWidth />
                            {!props.hideText && '排序看板'}
                        </div>
                    </Tooltip>
                }
                onClick={props.navigateTo('/car-queue-dashboard')}
            />
            <TreeItem
                itemId='price-list'
                label={
                    <Tooltip
                        title={<Typography variant={'h6'}>{'價目表'}</Typography>}
                        placement='right'
                        disableHoverListener={!props.hideText}
                    >
                        <div style={{ display: 'inline-block' }}>
                            <FontAwesomeIcon icon={faFileInvoiceDollar} fixedWidth />
                            {!props.hideText && '價目表'}
                        </div>
                    </Tooltip>
                }
                onClick={props.navigateTo('/price-list')}
            />
            {props.createMenu(props.menuConfigs)}
            {props.auth.isAuth ? (
                <TreeItem
                    itemId='sign-out'
                    label={
                        <Tooltip
                            title={<Typography variant={'h6'}>{'登出'}</Typography>}
                            placement='right'
                            disableHoverListener={!props.hideText}
                        >
                            <div style={{ display: 'inline-block' }}>
                                <FontAwesomeIcon icon={faSignOut} fixedWidth />
                                {!props.hideText && '登出'}
                            </div>
                        </Tooltip>
                    }
                    onClick={props.auth.signOut}
                />
            ) : (
                <TreeItem
                    itemId='sign-in'
                    label={
                        <Tooltip
                            title={<Typography variant={'h6'}>{'登入'}</Typography>}
                            placement='right'
                            disableHoverListener={!props.hideText}
                        >
                            <div style={{ display: 'inline-block' }}>
                                <FontAwesomeIcon icon={faSignIn} fixedWidth />
                                {!props.hideText && '登入'}
                            </div>
                        </Tooltip>
                    }
                    onClick={props.navigateTo('/login')}
                />
            )}
        </SimpleTreeView>
    ) : (
        <></>
    );
};
